/**
 * **状态机接线** —— 从 Playground.vue 抽出来。
 *
 * ## 这个文件负责什么
 *
 * `graph/canvasGesture.ts` 是个**纯状态机**：自己不碰 DOM、不碰图引擎
 * （所以它能脱离浏览器测，见 `scripts/verify-canvas-gesture.ts`）。
 * 这里就是「把宿主喂给它」和「把它吐出来的命令执行掉」两半：
 *
 *   `gestureContext`       状态机需要的外部信息（找边 / 命中端口 / 划痕命中…）
 *   `applyConnection`      真把一条连线落到图上（结构校验 + 覆盖语义 + 类型推导）
 *   `putEdgeBack`          把摘下来的线放回图上（撤销断开）
 *   `runGestureCommands`   执行状态机吐出来的命令
 *   `sendGesture`          喂一个事件进去
 *
 * ## ⚠️ 抽出来的方式：函数体逐字不动
 *
 * `menu` / `menuPicked` 由宿主注入 —— 菜单状态比这段声明得早
 * （`useAddNode` 也要读菜单落点），所以三个都留在宿主。
 */
import { nextTick } from 'vue'
/* ⚠️ 列表操作是我们自己的（`edgeOps.ts`）—— 换引擎时这两个 import 不用改 */
import { addEdge, edgeOfConnection } from '@/graph/edgeOps'
import {
  findCutTargets,
  simplifyPolyline,
} from '@/graph/cutGeometry'
import type { CutCandidate } from '@/graph/cutGeometry'
import { reduce } from '@/graph/canvasGesture'
import { FLOW_DOM, HANDLE_ID_ATTR, HANDLE_NODE_ATTR } from '@/graph/graphDom'
import type {
  EdgeRef,
  GestureCommand,
  GestureContext,
  GestureEvent,
  GestureState,
} from '@/graph/canvasGesture'
import { findEdgeOnInput, sameHandle, withoutTargetEdge } from '@/graph/connectionRules'
import type { Point } from '@/graph/cutGeometry'
import { themeSocketColor } from '@/theme/runtime'
import type { SocketType } from '@/types/socket'
import type { FlowStoreApi } from '@/graph/flowStore'
/* ⚠️ 结构类型，不是引擎的类型（`flowTypes.ts`） */
import type { FlowConnection as Connection, FlowEdge as Edge } from '@/graph/flowTypes'

export interface GestureMachineCtx {
  gesture: { value: GestureState }
  edges: { value: Edge[] }
  flowStore: { value: FlowStoreApi | null }
  /** 画布容器 —— 用闭包注入（它声明得比这段晚） */
  graphRef: () => HTMLElement | null
  /** 菜单状态 —— 这里只写它的 `link`，所以放宽成可索引对象 */
  menu: { value: Record<string, unknown> | null }
  edgeViews: () => {
    id?: string
    muted?: boolean
    source: string
    sourceHandle?: string | null
    target: string
    targetHandle?: string | null
  }[]
  collectEdgeCandidates: () => CutCandidate<string>[]
  toFlowPoints: (points: readonly Point[]) => Point[]
  socketTypeOf: (nodeId?: string | null, handleId?: string | null) => SocketType | undefined
  isStructurallyValid: (
    s: string,
    sh: string | null | undefined,
    t: string,
    th: string | null | undefined,
  ) => boolean
  alreadyConnected: (
    s: string,
    sh: string | null | undefined,
    t: string,
    th: string | null | undefined,
  ) => boolean
  inferListItemType: (
    s: string,
    sh: string | null | undefined,
    t: string,
    th: string | null | undefined,
  ) => void
  makeEdge: (source: string, sourceHandle: string, target: string, targetHandle: string) => Edge
  repointConnectionLine: (nodeId: string, handleId: string) => void
  menuPicked: { value: boolean }
  commit: (label: string, coalesceMs?: number) => void
  showNotice: (message: string, actions?: NoticeActionInput | NoticeActionInput[]) => void
}

/** 提示条动作 —— 只描述这个文件用得到的形状 */
export interface NoticeActionInput {
  label: string
  run: () => void
}

export function useGestureMachine(ctx: GestureMachineCtx) {
  const {
    gesture,
    edges,
    flowStore,
    graphRef,
    menu,
    edgeViews,
    collectEdgeCandidates,
    toFlowPoints,
    socketTypeOf,
    isStructurallyValid,
    alreadyConnected,
    inferListItemType,
    makeEdge,
    repointConnectionLine,
    menuPicked,
    commit,
    showNotice,
  } = ctx

/* ── 状态机接线 ─────────────────────────────────────────────── */

/**
 * 状态机需要的外部信息，全部从这里注入。
 *
 * 状态机自己不碰 DOM、不碰图引擎 —— 所以它能脱离浏览器测
 * （见 scripts/verify-canvas-gesture.ts）。
 */
const gestureContext: GestureContext = {
  edgeOnInput(nodeId, handleId) {
    const edge = findEdgeOnInput(edgeViews(), nodeId, handleId) as unknown as
      | { id?: string; source: string; sourceHandle?: string | null; target: string; targetHandle?: string | null }
      | undefined
    if (!edge?.id) return undefined
    return {
      id: edge.id,
      source: edge.source,
      sourceHandle: edge.sourceHandle ?? null,
      target: edge.target,
      targetHandle: edge.targetHandle ?? null,
    }
  },

  /** 松手时指针底下是哪个端口 */
  handleAt(point) {
    /* ⚠️ 选择器从 `graphDom.ts` 取（引擎的 DOM 契约），属性名同理 */
    const el = (
      document.elementFromPoint(point.x, point.y) as HTMLElement | null
    )?.closest?.(`.${FLOW_DOM.handle}`) as HTMLElement | null
    const nodeId = el?.getAttribute(HANDLE_NODE_ATTR)
    const handleId = el?.getAttribute(HANDLE_ID_ATTR)
    if (!nodeId || !handleId) return null
    return {
      nodeId,
      handleId,
      type: el?.classList.contains('target') ? 'target' : 'source',
    }
  },

  canDrop(from, to) {
    /*
     * ⚠️ 这里只用**结构校验**，不含「是否已经连过」——
     * 与交给 Vue Flow 的 `isValidConnection` 保持一致（见 useGestureState 的注释）。
     * 拔线改接时原边已经被摘掉了，所以「已连过」的判定在这里也不该成立。
     */
    return isStructurallyValid(from.nodeId, from.handleId, to.nodeId, to.handleId)
  },

  /** 划痕扫到了哪些边（几何在 graph/cutGeometry.ts） */
  hitTest(path) {
    const host = graphRef()
    if (!host) return []
    const rect = host.getBoundingClientRect()
    const local = simplifyPolyline(
      path.map((p) => ({ x: p.x - rect.left, y: p.y - rect.top })),
      4,
    )
    return findCutTargets(toFlowPoints(local), collectEdgeCandidates(), 6)
  },

  isEdgeMuted(edgeId) {
    const edge = (
      edgeViews() as unknown as { id?: string; muted?: boolean }[]
    ).find((e) => e.id === edgeId)
    return Boolean(edge?.muted)
  },
}

/** 真正把一条连线落到图上：结构校验、替换语义、列表类型推导都在这 */
function applyConnection(conn: {
  source: string
  sourceHandle: string | null
  target: string
  targetHandle: string | null
}) {
  if (
    !isStructurallyValid(conn.source, conn.sourceHandle, conn.target, conn.targetHandle)
  ) {
    return
  }
  if (alreadyConnected(conn.source, conn.sourceHandle, conn.target, conn.targetHandle)) {
    showNotice(
      `这条连线已经存在：${conn.source}.${conn.sourceHandle} → ${conn.target}.${conn.targetHandle}`,
    )
    return
  }

  const type = socketTypeOf(conn.source, conn.sourceHandle) ?? 'any'
  const edge: Edge = {
    /* ⚠️ id 的命名约定只从 `edgeOps.edgeIdOf` 来（原来五处各写一遍） */
    ...edgeOfConnection(conn),
    style: { stroke: themeSocketColor(type) },
    data: { socketType: type },
  }

  const previous = findEdgeOnInput(edgeViews(), conn.target, conn.targetHandle)
  const isReroute =
    previous &&
    (previous.source !== conn.source ||
      !sameHandle(previous.sourceHandle, conn.sourceHandle))

  // 一个输入只接一条边（v5 契约）→ 覆盖语义。用纯函数而不是内联 filter：
  // handle 的 null/undefined/'' 等价比较必须和图引擎一致。
  const kept = withoutTargetEdge(edgeViews(), conn.target, conn.targetHandle)
  /*
   * ⚠️ `addEdge` 是我们自己的（`edgeOps.ts`）：同 id 已存在就不重复加。
   * 转一手是因为 `kept` 的元素类型是**更松的** `EdgeRef`（它的 id 可选 ——
   * 那是"可能来自任何地方的一行"的形状），而这里手上这份**一定有 id**
   * （它来自文档）。运行期是同一种东西，类型上差一个 `?`。
   */
  edges.value = addEdge(edge, kept as unknown as Edge[]) as unknown as Edge[]

  inferListItemType(conn.source, conn.sourceHandle, conn.target, conn.targetHandle)

  if (isReroute && previous) {
    showNotice(
      `已改接 ${conn.targetHandle}：来源从 ${previous.source}.${previous.sourceHandle} 换成 ${conn.source}.${conn.sourceHandle}`,
    )
  }

  nextTick(() => flowStore.value?.updateNodeInternals([conn.source, conn.target]))
}

/** 把摘下来的线放回图上（撤销断开 / 落回原位都用它） */
function putEdgeBack(edge: EdgeRef) {
  edges.value = [
    ...(edgeViews() as unknown as Record<string, unknown>[]),
    makeEdge(
      edge.source,
      edge.sourceHandle ?? '',
      edge.target,
      edge.targetHandle ?? '',
    ) as unknown as Record<string, unknown>,
  ] as unknown as Edge[]
  nextTick(() => flowStore.value?.updateNodeInternals([edge.target]))
}

/** 执行状态机吐出来的命令。状态机只提议，动手的是这里 */
function runGestureCommands(commands: readonly GestureCommand[]) {
  type Loose = { id?: string; muted?: boolean; [key: string]: unknown }

  for (const cmd of commands) {
    switch (cmd.type) {
      case 'removeEdge':
        commit('断开连线')
        edges.value = (edges.value as unknown as Loose[]).filter(
          (e) => e.id !== cmd.edgeId,
        ) as unknown as Edge[]
        nextTick(() => flowStore.value?.updateNodeInternals())
        break

      case 'addEdge':
        commit('连线')
        applyConnection(cmd)
        break

      case 'repointConnectionLine':
        repointConnectionLine(cmd.from.nodeId, cmd.from.handleId)
        break

      case 'cutEdges': {
        commit('剪断连线')
        const hit = new Set(cmd.edgeIds)
        edges.value = (edges.value as unknown as Loose[]).filter(
          (e) => !hit.has(e.id ?? ''),
        ) as unknown as Edge[]
        nextTick(() => flowStore.value?.updateNodeInternals())
        break
      }

      case 'setEdgeMuted': {
        commit('静音连线')
        const hit = new Set(cmd.edgeIds)
        edges.value = (edges.value as unknown as Loose[]).map((e) =>
          hit.has(e.id ?? '') ? { ...e, muted: cmd.muted } : e,
        ) as unknown as Edge[]
        break
      }

      case 'offerLinkCandidates':
        menuPicked.value = false
        menu.value = {
          kind: 'link',
          x: cmd.point.x,
          y: cmd.point.y,
          link: { origin: cmd.origin, point: cmd.point, undoEdge: cmd.undoEdge },
        }
        break

      case 'notice': {
        const undoEdge = cmd.undoEdge
        showNotice(
          cmd.message,
          undoEdge ? { label: '撤销', run: () => putEdgeBack(undoEdge) } : undefined,
        )
        break
      }
    }
  }
}

function sendGesture(event: GestureEvent) {
  const transition = reduce(gesture.value, event, { context: gestureContext })
  gesture.value = transition.state
  if (transition.commands.length > 0) runGestureCommands(transition.commands)
}

  return { gestureContext, applyConnection, putEdgeBack, runGestureCommands, sendGesture }
}
